import { useMemo, useState, useRef, useEffect } from 'react';
import { Save, MoreHorizontal, Pencil, Trash2, PlusCircle } from 'lucide-react';
import { Combobox } from '@/features/ui/components/Combobox';
import { Input } from '@/features/ui/components/Input';
import { InfoTooltip } from '@/features/ui/components/Tooltip';
import { useMappingsStore } from '../store/mappings.store';
import { useCrmStore } from '../store/crm.store';
import DeleteMappingModal from './DeleteMappingModal';

interface FormBuilderHeaderProps {
  onSave: () => Promise<void>;
  onCreateNewMapping: () => void;
}

const FormBuilderHeader = ({ onSave, onCreateNewMapping }: FormBuilderHeaderProps) => {
  const {
    isDrafting,
    currentMappingName,
    currentMappingPrimaryObject,
    setCurrentMappingName,
    setCurrentMappingPrimaryObject,
    mappingSummaries,
    currentFieldMappingId,
    setCurrentFieldMappingId,
    deleteFieldMapping,
  } = useMappingsStore();
  const { supportedCrmObjects } = useCrmStore();

  // Local state for dropdown menu and rename mode
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameMode, setRenameMode] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const crmObjectOptions = useMemo(
    () =>
      supportedCrmObjects.map((obj) => ({
        value: obj.id,
        label: obj.label,
      })),
    [supportedCrmObjects]
  );

  const mappingOptions = useMemo(
    () =>
      mappingSummaries.map((m) => ({
        value: m.mapping_id,
        label: m.mapping_name,
      })),
    [mappingSummaries]
  );

  return (
    <header className="bg-white border-b border-gray-200 p-6 sticky top-0 z-20">
      <DeleteMappingModal
        isOpen={deleteOpen}
        mappingName={
          mappingSummaries.find((m) => m.mapping_id === currentFieldMappingId)?.mapping_name ||
          (currentMappingName || '')
        }
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          if (currentFieldMappingId) {
            deleteFieldMapping(currentFieldMappingId);
          }
          setDeleteOpen(false);
        }}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {isDrafting ? 'Create New Field Mapping' : 'Edit Field Mapping'}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={onCreateNewMapping}
            className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            New Mapping
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            Save & Test
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="mapping-name-input"
            className="text-sm font-medium text-gray-700 flex items-center h-5"
          >
            Mapping Name
          </label>
          <div className="mt-1 relative" ref={menuRef}>
            {renameMode ? (
              <Input
                id="mapping-name-input"
                type="text"
                value={currentMappingName || ''}
                onChange={(e) => setCurrentMappingName(e.target.value)}
                placeholder="Enter mapping name"
                className=""
                onBlur={() => {
                  // Exit rename mode on blur so the combobox becomes usable again
                  setRenameMode(false);
                }}
              />
            ) : (
              <Combobox
                value={currentFieldMappingId || ''}
                onChange={(val) => setCurrentFieldMappingId(val || null)}
                options={mappingOptions}
                placeholder={
                  // For new/draft mappings (no ID yet), show the current name as the label
                  // so it doesn't revert to "Select a Mapping" after renaming.
                  currentMappingName ||
                  (mappingOptions.length ? 'Select a Mapping' : 'No mappings yet')
                }
                searchPlaceholder="Search mappings..."
                noResultsMessage="No mappings found."
                triggerClassName="pr-10"
              />
            )}
            <button
              aria-label="mapping actions"
              onClick={() => setMenuOpen((o) => !o)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100"
            >
              <MoreHorizontal className="h-4 w-4 text-gray-600" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-36 bg-white border rounded-md shadow-md z-10">
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                  onClick={() => {
                    setMenuOpen(false);
                    setRenameMode(true);
                  }}
                >
                  <Pencil className="h-4 w-4" /> Rename
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setMenuOpen(false);
                    setDeleteOpen(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
        <div>
          <label
            htmlFor="primary-object-selector"
            className="text-sm font-medium text-gray-700 flex items-center h-5"
          >
            Primary Object
            <InfoTooltip
              className="ml-1"
              text="The primary CRM object is the main record that the form will interact with, such as Lead or Account. All other related objects are accessed through this primary object."
            />
          </label>
          <div className="mt-1">
            <Combobox
              value={currentMappingPrimaryObject || ''}
              onChange={setCurrentMappingPrimaryObject}
              options={crmObjectOptions}
              placeholder="Select an Object"
              searchPlaceholder="Search objects..."
              noResultsMessage="No objects found."
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default FormBuilderHeader;
